"use client";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import type { WebMcpCopy } from "@/lib/content/entries/webmcp";
import { WebMcpContext } from "@/lib/webmcp/react";
import { WebMcpRuntime, type ModelContext, type WebMcpActionPolicy } from "@/lib/webmcp/runtime";
import { track } from "@/lib/posthog/events";

type Review = { description: string; details: string; finish: (approved: boolean) => void };
export default function WebMcpProvider({ children, copy, enabled }: { children: ReactNode; copy: WebMcpCopy; enabled: boolean }) {
  const route = usePathname() ?? "/";
  const currentRoute = useRef(route);
  useLayoutEffect(() => { currentRoute.current = route; }, [route]);
  const [runtime, setRuntime] = useState<WebMcpRuntime | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const model = (document as Document & { modelContext?: ModelContext }).modelContext;
    if (!enabled || !window.isSecureContext || window.top !== window.self || !model?.registerTool) return;
    let active: WebMcpRuntime | null = null;
    let activeContext: "normal" | "privacy_dialog" | null = null;
    let hidden = false;
    let disposed = false;
    const context = () => document.documentElement.hasAttribute("data-cookie-consent-open") ? "privacy_dialog" : "normal";
    const actionAllowed: WebMcpActionPolicy = (action) => context() === "privacy_dialog"
      ? action === "close_privacy_dialog"
      : action !== "close_privacy_dialog";
    const refresh = () => {
      if (disposed) return;
      const allowed = !hidden && !document.documentElement.hasAttribute("data-adult-gate-pending");
      const nextContext = context();
      if (!allowed) { active?.dispose(); active = null; activeContext = null; setRuntime(null); return; }
      if (active && activeContext === nextContext) return;
      // Recreate only our runtime. Its abort signals synchronously revoke previous
      // native registrations and cancel in-flight work before the new context binds.
      active?.dispose();
      active = new WebMcpRuntime(model, copy, route, (description, details, signal) => new Promise<boolean>((resolve) => {
      if (document.documentElement.hasAttribute("data-cookie-consent-open") || document.body.classList.contains("mobile-nav-active")) { resolve(false); return; }
      let done = false;
      const finish = (approved: boolean) => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        signal.removeEventListener("abort", cancel);
        setReview(null);
        resolve(approved && !signal.aborted);
      };
      const cancel = () => finish(false);
      const timeout = setTimeout(cancel, 120_000);
      signal.addEventListener("abort", cancel, { once: true });
      if (signal.aborted) cancel();
      else setReview({ description, details, finish });
      }), () => !hidden && !disposed && currentRoute.current === route && !document.documentElement.hasAttribute("data-adult-gate-pending"),
      (event) => track("webmcp_action_result", {
        webmcp_action: event.action, webmcp_risk: event.risk,
        webmcp_outcome: event.outcome, webmcp_duration_bucket: event.duration,
      }), actionAllowed);
      activeContext = nextContext;
      setRuntime(active);
    };
    const hide = () => { hidden = true; refresh(); };
    const show = () => { hidden = false; refresh(); };
    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-adult-gate-pending", "data-cookie-consent-open"] });
    window.addEventListener("pagehide", hide);
    window.addEventListener("pageshow", show);
    queueMicrotask(refresh);
    return () => {
      disposed = true;
      observer.disconnect();
      window.removeEventListener("pagehide", hide);
      window.removeEventListener("pageshow", show);
      active?.dispose();
    };
  }, [copy, enabled, route]);
  useEffect(() => {
    const element = dialog.current;
    if (review && element && !element.open) element.showModal();
    if (!review && element?.open) element.close();
  }, [review]);
  return <WebMcpContext.Provider value={runtime}>
    {children}
    <dialog ref={dialog} aria-labelledby="webmcp-review-title" aria-describedby="webmcp-review-body webmcp-review-action" onCancel={() => review?.finish(false)} onClose={() => review?.finish(false)} style={{ boxSizing: "border-box", width: "min(36rem, 92vw)", maxWidth: "92vw", maxHeight: "85dvh", overflowY: "auto", padding: "1.5rem", borderRadius: "1rem" }}>
      <h2 id="webmcp-review-title">{copy.reviewTitle}</h2>
      <p id="webmcp-review-body">{copy.reviewBody}</p><p id="webmcp-review-action">{review?.description}</p>
      <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: "40vh", overflow: "auto" }}>{review?.details}</pre>
      <button type="button" autoFocus onClick={() => review?.finish(false)}>{copy.cancel}</button>{" "}
      <button type="button" onClick={(event) => { if (event.nativeEvent.isTrusted) review?.finish(true); }}>{copy.confirm}</button>
    </dialog>
  </WebMcpContext.Provider>;
}
